import { useEffect, useRef, useState } from 'react';
import type { ParkingSpot, SeekerPin, RentPin } from '../types';
import { PARKING_LABELS, VEHICLE_LABELS, DURATION_LABELS } from '../types';
import { trackEvent } from '../services/analytics';
import type { PinType } from '../services/analytics';

const CDN_STYLE_JSON = 'https://api.olamaps.io/tiles/vector/v1/styles/default-dark-standard/style.json?key=0.4.0';

interface MapProps {
  spots: ParkingSpot[];
  seekers: SeekerPin[];
  rentPins: RentPin[];
  viewMode: 'all' | 'spots' | 'seekers' | 'rent';
  onMapClick?: (lat: number, lng: number) => void;
  isAddingPin?: boolean;
  initialCenter?: [number, number];
  initialZoom?: number;
  focusedPin?: { type: PinType; id: string } | null;
  onReady?: (ready: boolean) => void;
}

const createCircleElement = (color: string, scale: number = 10): HTMLDivElement => {
  const el = document.createElement('div');
  el.style.width = `${scale * 2}px`;
  el.style.height = `${scale * 2}px`;
  el.style.borderRadius = '50%';
  el.style.backgroundColor = color;
  el.style.border = '2px solid rgba(255,255,255,0.9)';
  el.style.boxShadow = `0 0 14px ${color}80, 0 2px 8px rgba(0,0,0,0.5)`;
  el.style.cursor = 'pointer';
  return el;
};

const createSpotMarkerElement = (spot: ParkingSpot): HTMLDivElement => {
  const price = Number(spot.price_hourly);
  const isFree = price === 0;
  const hasPrice = price > 0;
  const bgColor = isFree ? '#16a34a' : '#d97706';
  const glowColor = isFree ? 'rgba(22,163,74,0.6)' : 'rgba(217,119,6,0.55)';

  const el = document.createElement('div');
  el.style.display = 'flex';
  el.style.flexDirection = 'column';
  el.style.alignItems = 'center';
  el.style.cursor = 'pointer';
  el.style.userSelect = 'none';

  const label = document.createElement('div');
  label.style.padding = '4px 10px';
  label.style.borderRadius = '8px';
  label.style.fontSize = '11px';
  label.style.fontWeight = '700';
  label.style.color = 'white';
  label.style.fontFamily = '-apple-system,BlinkMacSystemFont,sans-serif';
  label.style.backgroundColor = bgColor;
  label.style.border = `1px solid ${bgColor}99`;
  label.style.boxShadow = `0 0 16px ${glowColor}, 0 2px 8px rgba(0,0,0,0.45)`;
  label.style.lineHeight = '1.2';
  label.textContent = isFree ? 'Free' : hasPrice ? `₹${price}/hr` : '';

  const caret = document.createElement('div');
  caret.style.width = '8px';
  caret.style.height = '8px';
  caret.style.transform = 'rotate(45deg)';
  caret.style.marginTop = '-4px';
  caret.style.backgroundColor = bgColor;

  el.appendChild(label);
  el.appendChild(caret);
  return el;
};

const createSpotPopupHTML = (spot: ParkingSpot): string => {
  const hourly = Number(spot.price_hourly);
  const daily = Number(spot.price_daily);
  const monthly = Number(spot.price_monthly);
  const prices: string[] = [];
  if (hourly === 0) prices.push('Free / hr');
  else if (!isNaN(hourly)) prices.push(`₹${hourly}/hr`);
  if (!isNaN(daily) && daily > 0) prices.push(`₹${daily}/day`);
  if (!isNaN(monthly) && monthly > 0) prices.push(`₹${monthly}/mo`);

  const vehicles = spot.vehicle_types.map((v) => VEHICLE_LABELS[v]).join(', ');
  const security = spot.security_features.length > 0
    ? spot.security_features.map((s) => s === 'cctv' ? 'CCTV' : s === 'guard' ? 'Guard' : 'Gated').join(', ')
    : 'None';

  const interestCount = spot.interest_count ?? 0;
  const commentCount = spot.comment_count ?? 0;
  const safeId = String(spot.id).replace(/[^a-zA-Z0-9-]/g, '');

  return `
    <div style="padding:8px;max-width:240px;font-family:system-ui,sans-serif;font-size:13px;">
      <div style="font-weight:600;margin-bottom:4px;">${PARKING_LABELS[spot.parking_type]}</div>
      <div style="color:#22c55e;font-weight:700;margin-bottom:4px;">${prices.join(' · ') || 'Price not set'}</div>
      <div style="font-size:12px;color:#9ca3af;margin-bottom:2px;">${vehicles} · ${spot.is_covered ? 'Covered' : 'Uncovered'}</div>
      <div style="font-size:12px;color:#9ca3af;margin-bottom:2px;">Security: ${security}</div>
      ${spot.ev_charging ? '<div style="font-size:12px;color:#9ca3af;">EV Charging</div>' : ''}
      ${spot.building_name ? `<div style="font-size:12px;color:#9ca3af;margin-top:2px;">${spot.building_name}</div>` : ''}
      ${spot.description ? `<div style="font-size:12px;color:#9ca3af;margin-top:2px;">${spot.description}</div>` : ''}
      <div style="font-size:11px;color:#6b7280;margin-top:6px;">Drop a seeker pin to get matched via email</div>
      <div style="margin-top:8px;display:flex;gap:6px;">
        <button type="button" data-interest data-spot-id="${safeId}"
                style="flex:1;font-size:12px;font-weight:600;color:white;background:#d97706;border:none;border-radius:6px;padding:6px 8px;cursor:pointer;font-family:inherit;">
          I'm interested
        </button>
        <button type="button" data-comments data-pin-type="spot" data-pin-id="${safeId}"
                style="flex:0 0 auto;font-size:12px;font-weight:600;color:#374151;background:#f3f4f6;border:none;border-radius:6px;padding:6px 10px;cursor:pointer;font-family:inherit;display:flex;align-items:center;gap:4px;"
                title="View comments">
          <span>💬</span><span>${commentCount}</span>
        </button>
      </div>
      ${interestCount > 0 ? `<div style="font-size:11px;color:#6b7280;margin-top:4px;">💌 ${interestCount} ${interestCount === 1 ? 'person is' : 'people are'} interested</div>` : ''}
      ${buildPopupActionsHTML('spot', spot.id, '#d97706')}
    </div>
  `;
};

const createSeekerPopupHTML = (seeker: SeekerPin): string => {
  const vehicles = seeker.vehicle_types.map((v) => VEHICLE_LABELS[v]).join(', ');
  const commentCount = seeker.comment_count ?? 0;
  const safeId = String(seeker.id).replace(/[^a-zA-Z0-9-]/g, '');

  return `
    <div style="padding:8px;max-width:220px;font-family:system-ui,sans-serif;font-size:13px;">
      <div style="font-weight:600;margin-bottom:4px;">Looking for parking</div>
      <div style="font-size:12px;color:#9ca3af;margin-bottom:4px;">
        ${vehicles} · ${DURATION_LABELS[seeker.duration_needed]}
      </div>
      ${seeker.budget_max ? `<div style="color:#60a5fa;font-weight:600;font-size:12px;">Budget: up to ₹${seeker.budget_max}</div>` : ''}
      ${seeker.requires_covered ? '<div style="font-size:11px;color:#9ca3af;">Requires covered parking</div>' : ''}
      <div style="margin-top:8px;display:flex;gap:6px;">
        <button type="button" data-comments data-pin-type="seeker" data-pin-id="${safeId}"
                style="font-size:12px;font-weight:600;color:#374151;background:#f3f4f6;border:none;border-radius:6px;padding:5px 10px;cursor:pointer;font-family:inherit;display:flex;align-items:center;gap:4px;"
                title="View comments">
          <span>💬</span><span>${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}</span>
        </button>
      </div>
      ${buildShareButtonsHTML('seeker', seeker.id, '#2563eb')}
    </div>
  `;
};

const createRentPopupHTML = (pin: RentPin): string => {
  const hourly = Number(pin.price_hourly);
  const daily = Number(pin.price_daily);
  const monthly = Number(pin.price_monthly);
  const prices: string[] = [];
  if (hourly === 0) prices.push('Free');
  else if (!isNaN(hourly)) prices.push(`₹${hourly}/hr`);
  if (!isNaN(daily) && daily > 0) prices.push(`₹${daily}/day`);
  if (!isNaN(monthly) && monthly > 0) prices.push(`₹${monthly}/mo`);

  const commentCount = pin.comment_count ?? 0;
  const safeId = String(pin.id).replace(/[^a-zA-Z0-9-]/g, '');

  return `
    <div style="padding:8px;max-width:220px;font-family:system-ui,sans-serif;font-size:13px;">
      <div style="font-weight:600;margin-bottom:4px;">What people pay here</div>
      <div style="color:#a78bfa;font-weight:700;margin-bottom:4px;">${prices.join(' · ') || 'Price not set'}</div>
      <div style="font-size:12px;color:#9ca3af;">
        ${VEHICLE_LABELS[pin.vehicle_type]} · ${PARKING_LABELS[pin.parking_type]}
      </div>
      <div style="margin-top:8px;display:flex;gap:6px;">
        <button type="button" data-comments data-pin-type="rent" data-pin-id="${safeId}"
                style="font-size:12px;font-weight:600;color:#374151;background:#f3f4f6;border:none;border-radius:6px;padding:5px 10px;cursor:pointer;font-family:inherit;display:flex;align-items:center;gap:4px;"
                title="View comments">
          <span>💬</span><span>${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}</span>
        </button>
      </div>
      ${buildPopupActionsHTML('rent', pin.id, '#7c3aed')}
    </div>
  `;
};

const buildShareButtonsHTML = (type: PinType, id: string, accent: string): string => {
  const safeId = String(id).replace(/[^a-zA-Z0-9-]/g, '');
  return `
    <div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;display:flex;gap:6px;">
      <button type="button" data-share data-share-method="copy_link" data-pin-type="${type}" data-pin-id="${safeId}"
              style="flex:1;font-size:11px;font-weight:600;color:white;background:${accent};border:none;border-radius:6px;padding:5px 8px;cursor:pointer;font-family:inherit;">
        Copy link
      </button>
      <button type="button" data-share data-share-method="whatsapp" data-pin-type="${type}" data-pin-id="${safeId}"
              style="flex:1;font-size:11px;font-weight:600;color:white;background:#16a34a;border:none;border-radius:6px;padding:5px 8px;cursor:pointer;font-family:inherit;">
        WhatsApp
      </button>
    </div>
  `;
};

const buildPopupActionsHTML = (
  type: 'spot' | 'rent',
  id: string,
  accent: string,
): string => {
  const safeId = String(id).replace(/[^a-zA-Z0-9-]/g, '');
  return `
    <div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;display:flex;gap:6px;align-items:stretch;">
      <button type="button" data-share data-share-method="copy_link" data-pin-type="${type}" data-pin-id="${safeId}"
              style="flex:1;font-size:11px;font-weight:600;color:white;background:${accent};border:none;border-radius:6px;padding:5px 8px;cursor:pointer;font-family:inherit;">
        Copy link
      </button>
      <button type="button" data-share data-share-method="whatsapp" data-pin-type="${type}" data-pin-id="${safeId}"
              style="flex:1;font-size:11px;font-weight:600;color:white;background:#16a34a;border:none;border-radius:6px;padding:5px 8px;cursor:pointer;font-family:inherit;">
        WhatsApp
      </button>
      <button type="button" data-report data-pin-type="${type}" data-pin-id="${safeId}"
              title="Report this pin" aria-label="Report this pin"
              style="flex:0 0 32px;font-size:14px;color:#6b7280;background:#f3f4f6;border:none;border-radius:6px;padding:0;cursor:pointer;font-family:inherit;line-height:1;">
        🚩
      </button>
    </div>
  `;
};

const HIDDEN_POI_SOURCE_LAYERS = new Set(['poi']);

const hidePoiLayers = (map: any) => {
  const hide = () => {
    const layers = map.getStyle()?.layers;
    if (!layers) return;
    for (const layer of layers) {
      if (HIDDEN_POI_SOURCE_LAYERS.has(layer['source-layer'])) {
        map.setLayoutProperty(layer.id, 'visibility', 'none');
      }
    }
  };
  if (map.isStyleLoaded()) {
    hide();
  } else {
    map.once('style.load', hide);
  }
};

export default function Map({
  spots,
  seekers,
  rentPins,
  viewMode,
  onMapClick,
  isAddingPin,
  initialCenter = [78.9629, 20.5937],
  initialZoom = 5,
  focusedPin = null,
  onReady,
}: MapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const popupRef = useRef<any>(null);
  const clickHandlerRef = useRef<((e: any) => void) | null>(null);
  const lastFocusedPinRef = useRef<{ type: PinType; id: string } | null>(null);

  // We get the OlaMaps constructor from window after CDN load
  const getOlaMaps = () => {
    const w = window as any;
    return w.OlaMaps || w.olamaps?.OlaMaps || w.olamaps;
  };
  const getStyleJson = () => {
    const w = window as any;
    return w.defaultStyleJson || w.olamaps?.defaultStyleJson || CDN_STYLE_JSON;
  };
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    onReady?.(isLoaded);
  }, [isLoaded, onReady]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || mapRef.current) return;

    const apiKey = import.meta.env.VITE_OLA_MAPS_API_KEY;
    if (!apiKey) {
      setError('Ola Maps API key not configured. Add VITE_OLA_MAPS_API_KEY to your frontend/.env file.');
      return;
    }

    const OlaMapsClass = getOlaMaps();
    if (!OlaMapsClass) {
      setError('Ola Maps SDK not loaded. Check your network connection.');
      return;
    }

    const olaMaps = new OlaMapsClass({ apiKey });

    try {
      const map = olaMaps.init({
        style: getStyleJson(),
        container: container,
        center: initialCenter,
        zoom: initialZoom,
      });

      // init() may return a Promise or the map directly depending on build
      if (map && typeof map.then === 'function') {
        map.then((resolved: any) => {
          mapRef.current = resolved;
          hidePoiLayers(resolved);
          setIsLoaded(true);
          trackEvent('map_loaded', { provider: 'olamaps', result: 'success' });
        }).catch((err: unknown) => {
          console.error('Ola Maps init failed:', err);
          trackEvent('map_loaded', {
            provider: 'olamaps',
            result: 'error',
            error_kind: 'init_rejected',
          });
          setError('Failed to load Ola Maps. Please check your API key.');
        });
      } else if (map) {
        mapRef.current = map;
        hidePoiLayers(map);
        setIsLoaded(true);
        trackEvent('map_loaded', { provider: 'olamaps', result: 'success' });
      } else {
        trackEvent('map_loaded', {
          provider: 'olamaps',
          result: 'error',
          error_kind: 'init_returned_null',
        });
        setError('Ola Maps failed to initialize.');
      }
    } catch (err) {
      console.error('Ola Maps init failed:', err);
      trackEvent('map_loaded', {
        provider: 'olamaps',
        result: 'error',
        error_kind: 'init_threw',
      });
      setError('Failed to load Ola Maps. Please check your API key.');
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.flyTo({ center: initialCenter, zoom: initialZoom });
  }, [initialCenter, initialZoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (clickHandlerRef.current) {
      map.off('click', clickHandlerRef.current);
      clickHandlerRef.current = null;
    }

    if (isAddingPin && onMapClick) {
      const handler = (e: any) => {
        const { lng, lat } = e.lngLat;
        onMapClick(lat, lng);
      };
      clickHandlerRef.current = handler;
      map.on('click', handler);
      map.getCanvas().style.cursor = 'crosshair';
    } else {
      map.getCanvas().style.cursor = '';
    }
  }, [isAddingPin, onMapClick]);

  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    const map = mapRef.current;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    const OlaMapsClass: any = getOlaMaps();
    if (!OlaMapsClass) return;

    popupRef.current = new OlaMapsClass.Popup({ offset: 20, closeButton: true, closeOnClick: false });

    const markers: any[] = [];

    const addMarker = (lat: number, lng: number, color: string, scale: number, htmlContent: string, pinType: PinType) => {
      const marker = new OlaMapsClass.Marker({
        element: createCircleElement(color, scale),
        anchor: 'center',
      })
        .setLngLat([lng, lat])
        .addTo(map);

      marker.getElement().addEventListener('click', () => {
        trackEvent('map_popup_open', { pin_type: pinType });
        popupRef.current
          ?.setLngLat([lng, lat])
          .setHTML(htmlContent)
          .addTo(map);
      });

      markers.push(marker);
    };

    const addSpotMarker = (spot: ParkingSpot) => {
      const marker = new OlaMapsClass.Marker({
        element: createSpotMarkerElement(spot),
        anchor: 'center',
      })
        .setLngLat([spot.lng, spot.lat])
        .addTo(map);

      marker.getElement().addEventListener('click', () => {
        trackEvent('map_popup_open', { pin_type: 'spot' });
        popupRef.current
          ?.setLngLat([spot.lng, spot.lat])
          .setHTML(createSpotPopupHTML(spot))
          .addTo(map);
      });

      markers.push(marker);
    };

    if (viewMode === 'all') {
      spots.forEach((spot) => addSpotMarker(spot));
      seekers.forEach((s) => addMarker(s.lat, s.lng, '#2563eb', 9, createSeekerPopupHTML(s), 'seeker'));
      rentPins.forEach((p) => addMarker(p.lat, p.lng, '#7c3aed', 8, createRentPopupHTML(p), 'rent'));
    } else if (viewMode === 'spots') {
      spots.forEach((spot) => addSpotMarker(spot));
    } else if (viewMode === 'seekers') {
      seekers.forEach((s) => addMarker(s.lat, s.lng, '#2563eb', 9, createSeekerPopupHTML(s), 'seeker'));
    } else {
      rentPins.forEach((p) => addMarker(p.lat, p.lng, '#7c3aed', 8, createRentPopupHTML(p), 'rent'));
    }

    markersRef.current = markers;

    if (
      focusedPin &&
      (lastFocusedPinRef.current?.type !== focusedPin.type || lastFocusedPinRef.current?.id !== focusedPin.id)
    ) {
      const findFocused = (): { lat: number; lng: number; html: string } | null => {
        if (focusedPin.type === 'spot') {
          const pin = spots.find((s) => s.id === focusedPin.id);
          return pin ? { lat: pin.lat, lng: pin.lng, html: createSpotPopupHTML(pin) } : null;
        }
        if (focusedPin.type === 'seeker') {
          const pin = seekers.find((s) => s.id === focusedPin.id);
          return pin ? { lat: pin.lat, lng: pin.lng, html: createSeekerPopupHTML(pin) } : null;
        }
        const pin = rentPins.find((p) => p.id === focusedPin.id);
        return pin ? { lat: pin.lat, lng: pin.lng, html: createRentPopupHTML(pin) } : null;
      };

      const target = findFocused();
      if (target) {
        map.flyTo({ center: [target.lng, target.lat], zoom: 16 });
        popupRef.current
          ?.setLngLat([target.lng, target.lat])
          .setHTML(target.html)
          .addTo(map);
        lastFocusedPinRef.current = focusedPin;
      }
    }
  }, [spots, seekers, rentPins, viewMode, isLoaded, focusedPin]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-md">
          <div className="text-4xl mb-3">🗺️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Map Error</h3>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full" data-tour="map">
      <div
        id="map-container"
        ref={mapContainerRef}
        className="w-full h-full"
      />
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-parksy-600 mx-auto mb-3"></div>
            <p className="text-gray-600 text-sm">Loading Ola Maps...</p>
          </div>
        </div>
      )}
      {isAddingPin && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-navy-900 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-10">
          Tap on the map to drop your pin
        </div>
      )}
    </div>
  );
}
